import React from 'react';
import { useFormContext } from 'react-hook-form';
import { FormField } from '../atoms/FormField';
import { Input } from '../atoms/Input';
import { Textarea } from '../atoms/Textarea';
import { CryptoSelectField } from './CryptoSelectField';

/**
 * Date input field integrated with React Hook Form
 */
interface DateFieldProps {
  name: string;
  label?: string;
  required?: boolean;
  type?: 'datetime-local';
}

export const DateField: React.FC<DateFieldProps> = ({
  name,
  label = 'Date',
  required = false,
  type = 'datetime-local',
}) => {
  const { register, formState: { errors } } = useFormContext();
  const errorMessage = errors[name]?.message as string | undefined;

  return (
    <FormField label={label} error={errorMessage} required={required}>
      <Input
        type={type}
        {...register(name)}
        placeholder="Sélectionnez la date"
      />
    </FormField>
  );
};

/**
 * Amount input field integrated with React Hook Form
 */
interface AmountFieldProps {
  name: string;
  label?: string;
  required?: boolean;
  step?: string;
  placeholder?: string;
}

export const AmountField: React.FC<AmountFieldProps> = ({
  name,
  label = 'Quantité',
  required = false,
  step = '0.00000001',
  placeholder = 'Entrez la quantité',
}) => {
  const { register, formState: { errors } } = useFormContext();
  const errorMessage = errors[name]?.message as string | undefined;

  return (
    <FormField label={label} error={errorMessage} required={required}>
      <Input
        type="number"
        step={step}
        placeholder={placeholder}
        {...register(name, { valueAsNumber: true })}
      />
    </FormField>
  );
};

/**
 * Price input field integrated with React Hook Form
 */
interface PriceFieldProps {
  name: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  isFetching?: boolean;
  /** Quand fourni et `total > 0`, affiche une barre de progression (n requêtes de prix
   *  connues d'avance) au lieu du simple « Récupération… ». */
  fetchingProgress?: { done: number; total: number } | null;
}

export const PriceField: React.FC<PriceFieldProps> = ({
  name,
  label = 'Prix unitaire',
  required = false,
  placeholder = 'Entrez le prix',
  isFetching = false,
  fetchingProgress = null,
}) => {
  const { register, formState: { errors } } = useFormContext();
  const errorMessage = errors[name]?.message as string | undefined;

  const showProgressBar =
    isFetching && !!fetchingProgress && fetchingProgress.total > 0;
  const progressPct = showProgressBar
    ? Math.round((fetchingProgress!.done / fetchingProgress!.total) * 100)
    : 0;

  return (
    <FormField label={label} error={errorMessage} required={required}>
      <div className="relative">
        <Input
          type="number"
          step="any"
          placeholder={placeholder}
          {...register(name, { valueAsNumber: true })}
        />
        {isFetching && (
          <span className="text-xs text-blue-600 absolute top-2 right-2">
            {showProgressBar
              ? `${fetchingProgress!.done}/${fetchingProgress!.total}`
              : 'Récupération...'}
          </span>
        )}
        {showProgressBar && (
          <div className="absolute left-0 right-0 -bottom-1 h-0.5 bg-blue-100 rounded overflow-hidden">
            <div
              className="h-full bg-blue-500 transition-all duration-200"
              style={{ width: `${progressPct}%` }}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={fetchingProgress!.total}
              aria-valuenow={fetchingProgress!.done}
            />
          </div>
        )}
      </div>
    </FormField>
  );
};

/**
 * Crypto selection field integrated with React Hook Form
 */
interface CryptoFieldProps {
  name: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
}

export const CryptoField: React.FC<CryptoFieldProps> = ({
  name,
  label = 'Cryptomonnaie',
  required = false,
  placeholder = 'Sélectionnez une cryptomonnaie',
}) => {
  const { control } = useFormContext();

  return (
    <CryptoSelectField
      name={name}
      control={control}
      label={label}
      placeholder={placeholder}
      required={required}
    />
  );
};

/**
 * Transaction hash input field (optional)
 */
interface TransactionHashFieldProps {
  name: string;
  label?: string;
}

export const TransactionHashField: React.FC<TransactionHashFieldProps> = ({
  name,
  label = 'Hash de transaction',
}) => {
  const { register, formState: { errors } } = useFormContext();
  const errorMessage = errors[name]?.message as string | undefined;

  return (
    <FormField label={label} error={errorMessage}>
      <Input
        type="text"
        {...register(name)}
        placeholder="Hash de transaction (optionnel)"
      />
    </FormField>
  );
};

/**
 * Notes textarea field (optional)
 */
interface NotesFieldProps {
  name: string;
  label?: string;
  placeholder?: string;
}

export const NotesField: React.FC<NotesFieldProps> = ({
  name,
  label = 'Notes',
  placeholder = 'Notes additionnelles (optionnel)',
}) => {
  const { register, formState: { errors } } = useFormContext();
  const errorMessage = errors[name]?.message as string | undefined;

  return (
    <FormField label={label} error={errorMessage}>
      <Textarea
        {...register(name)}
        placeholder={placeholder}
        rows={3}
      />
    </FormField>
  );
};
